import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRemoveLocationFromWorkspace } from '@/queries/locations/useRemoveLocationFromWorkspace'
import { Globe, MapPin, Phone, Star, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { WorkspaceLocation } from 'honeygrid-types'

interface LocationRecordPanelProps {
	onClose: () => void
	location?: WorkspaceLocation
	isNew?: boolean
}

export function LocationRecordPanel({
	onClose,
	location,
	isNew = false,
}: LocationRecordPanelProps) {
	const { mutateAsync: removeLocation } = useRemoveLocationFromWorkspace()
	const [isDeleting, setIsDeleting] = useState(false)

	const handleDelete = async () => {
		if (!location) return

		setIsDeleting(true)
		try {
			await removeLocation({
				locationId: location.uuid,
			})
			toast.success('Location removed successfully')
			onClose()
		} catch (error) {
			console.error('Failed to remove location:', error)
			toast.error('Failed to remove location')
		} finally {
			setIsDeleting(false)
		}
	}

	const title_text = isNew ? 'Add Location' : location?.name || 'Location Details'

	if (isNew) {
		// For new locations, show search interface
		return (
			<div className="flex flex-col h-full">
				<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DrawerTitle>Add Location</DrawerTitle>
					<DrawerClose asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							className="hover:bg-primary hover:text-primary-foreground"
						>
							<X className="h-4 w-4" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				<div className="flex-1 px-4 overflow-auto">
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>Search for a business</Label>
							<Input
								placeholder="Enter business name or address..."
								// TODO: Wire up Google Places search
							/>
						</div>
						<div className="text-sm text-muted-foreground">
							Search for your business location using Google Places to add it to your workspace.
						</div>
					</div>
				</div>
			</div>
		)
	}

	// For existing locations, show details
	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>{title_text}</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				{location && (
					<div className="space-y-6">
						<div className="flex items-start gap-4">
							<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
								<MapPin className="h-6 w-6" />
							</div>
							<div className="flex-1">
								<h2 className="text-lg font-semibold">{location.name}</h2>
								<p className="text-sm text-muted-foreground">
									{Array.isArray(location.types) && location.types.length > 0
										? location.types[0]
										: 'Business'}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-4">
							<div className="space-y-2">
								<Label>Address</Label>
								<div className="flex items-center gap-2 text-sm">
									<MapPin className="h-4 w-4 text-muted-foreground" />
									<span>{location.address}</span>
								</div>
							</div>

							{location.phone_number && (
								<div className="space-y-2">
									<Label>Phone</Label>
									<div className="flex items-center gap-2 text-sm">
										<Phone className="h-4 w-4 text-muted-foreground" />
										<span>{location.phone_number}</span>
									</div>
								</div>
							)}

							<div className="space-y-2">
								<Label>Rating</Label>
								<div className="flex items-center gap-2">
									<Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
									<span className="text-sm">{location.rating}</span>
									<span className="text-xs text-muted-foreground">
										({location.ratings_total} reviews)
									</span>
								</div>
							</div>

							{location.website && (
								<div className="space-y-2">
									<Label>Website</Label>
									<div className="flex items-center gap-2 text-sm">
										<Globe className="h-4 w-4 text-muted-foreground" />
										<a
											href={location.website}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary hover:underline"
										>
											{location.website}
										</a>
									</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			<DrawerFooter>
				<Button
					variant="destructive"
					onClick={handleDelete}
					disabled={isDeleting}
					className="w-full"
				>
					{isDeleting ? 'Removing...' : 'Remove Location'}
				</Button>
			</DrawerFooter>
		</div>
	)
}
